import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getDatabase, getFormat, mcpToolsetUrl, parseData } from '../helpers.js';
import { confirm } from '../prompt.js';

export const toolsetsCommand = new Command('toolsets')
  .description('Manage MCP Factory toolsets');

toolsetsCommand.command('upsert <slug>')
  .description('Create or update an MCP toolset')
  .option('--name <name>', 'Toolset display name (required unless using --config)')
  .option('--description <desc>', 'Toolset description')
  .option('--tool <ref>', 'Reference a Tool by id, optionally renamed: <tool_id> or <tool_id>=<surface_name> (repeatable). Create Tools first with `rekor tools upsert`.', collect, [])
  .option('--relationship <spec>', 'Relationship tool spec: rel_type:op1,op2 (repeatable)', collect, [])
  .option('--batch <spec>', 'Batch operation spec: collection_or_rel:op1,op2 (repeatable)', collect, [])
  .option('--sql-query', 'Enable sql_query tool')
  .option('--config <json>', 'Full toolset config as JSON. A toolset composes first-class Tools by reference — its `tools` are refs {tool: <tool_id>, tool_name?, description_override?}. Create the Tools with `rekor tools upsert` (each carries its own collection, operation, and guard/shaping config: filterable_fields, writable_fields, precondition, binding). Use @file.json to read from file.')
  .option('--mint-token', 'After upsert, also mint a toolset-bound (consumer) token for this database and print it (handy for sandbox-testing the preview toolset)')
  .action(async function (this: Command, slug: string, opts: {
    name?: string;
    description?: string;
    tool: string[];
    relationship: string[];
    batch: string[];
    sqlQuery?: boolean;
    config?: string;
    mintToken?: boolean;
  }) {
    const ws = getDatabase(this);
    const client = new ApiClient();

    let body: Record<string, unknown>;

    if (opts.config) {
      // Full JSON config — a toolset composes Tools by reference ({tool, tool_name?, description_override?});
      // the shape config lives on the first-class Tools (authored via `rekor tools upsert`).
      // parseData also resolves @file.json (the help text advertised it; plain JSON.parse didn't).
      body = parseData(opts.config);
    } else {
      if (!opts.name) {
        throw new Error('--name is required (or use --config for full JSON config)');
      }
      body = {
        name: opts.name,
        tools: opts.tool.map(parseToolRef),
      };

      if (opts.description) body.description = opts.description;

      if (opts.relationship.length > 0) {
        body.relationships = opts.relationship.map(parseRelSpec);
      }

      if (opts.batch.length > 0) {
        const operations: Record<string, string[]> = {};
        for (const spec of opts.batch) {
          const parsed = parseSpec(spec);
          operations[parsed.key] = parsed.ops;
        }
        body.batch = { enabled: true, operations };
      }

      if (opts.sqlQuery) body.sql_query = true;
    }

    const data = await client.request('PUT', `/v1/${ws}/toolsets/${slug}`, body);
    const format = getFormat(this);
    console.log(formatOutput(data, format));

    // Convenience: mint a consumer token bound to the toolset just upserted. The
    // mint + the config write stay two distinct server operations; this only chains
    // the second call CLI-side. The toolset exists in `ws` now, so the binding resolves.
    if (opts.mintToken) {
      const tokenData = await client.request('POST', '/v1/tokens', {
        name: `${slug} consumer`,
        toolset_binding: { database_id: ws, toolset_id: slug },
      });
      if (format === 'json') {
        console.log(formatOutput(tokenData, format));
      } else {
        console.log('\nToolset-bound token:');
        console.log(formatOutput(tokenData, format));
        console.log(`MCP URL: ${mcpToolsetUrl(slug)}`);
      }
    }
  });

toolsetsCommand.command('get <slug>')
  .description('Get a toolset')
  .option('--resolved', 'Include resolved collection schemas')
  .action(async function (this: Command, slug: string, opts: { resolved?: boolean }) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const path = opts.resolved
      ? `/v1/${ws}/toolsets/${slug}/resolved`
      : `/v1/${ws}/toolsets/${slug}`;
    const data = await client.request('GET', path);
    console.log(formatOutput(data, getFormat(this)));
  });

toolsetsCommand.command('list')
  .description('List all toolsets')
  .action(async function (this: Command) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/toolsets`);
    console.log(formatOutput(data, getFormat(this)));
  });

toolsetsCommand.command('delete <slug>')
  .description('Delete a toolset')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, slug: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete toolset "${slug}"? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getDatabase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/toolsets/${slug}`);
    console.log('Deleted');
  });

toolsetsCommand.command('url <slug>')
  .description('Get the MCP connection URL for a toolset')
  .action((slug: string) => {
    console.log(mcpToolsetUrl(slug));
  });

// ---- Helpers ----

function collect(value: string, previous: string[]): string[] {
  return [...previous, value];
}

function parseSpec(spec: string): { key: string; ops: string[] } {
  const [key, opsStr] = spec.split(':');
  if (!key || !opsStr) {
    throw new Error(`Invalid spec '${spec}'. Expected format: name:op1,op2`);
  }
  return { key, ops: opsStr.split(',') };
}

/** A `--tool` ref: `<tool_id>` → {tool}, or `<tool_id>=<surface_name>` → {tool, tool_name}. */
export function parseToolRef(spec: string): Record<string, unknown> {
  const [tool, toolName] = spec.split('=');
  if (!tool) throw new Error(`Invalid --tool '${spec}'. Expected a Tool id, or <tool_id>=<surface_name>.`);
  return toolName ? { tool, tool_name: toolName } : { tool };
}

function parseRelSpec(spec: string): Record<string, unknown> {
  const { key, ops } = parseSpec(spec);
  return { rel_type: key, operations: ops };
}
